/**
 * RightSidebarContext - Context for right sidebar collapse state
 *
 * Provides independent collapse state management for right-side panels
 * Supports multiple right sidebars with unique storage keys
 *
 * Features:
 * - localStorage persistence per sidebar
 * - Independent state management
 * - Dark mode compatible
 * - Smooth 300ms transitions
 *
 * Usage:
 * const { isCollapsed, toggle } = useRightSidebar('calendar_sidebar');
 */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface RightSidebarContextProps {
  isCollapsed: boolean;
  toggle: () => void;
}

const RightSidebarContext = createContext<RightSidebarContextProps | undefined>(undefined);

interface RightSidebarProviderProps {
  children: ReactNode;
  storageKey: string;
  defaultCollapsed?: boolean;
}

export const RightSidebarProvider = ({
  children,
  storageKey,
  defaultCollapsed = false
}: RightSidebarProviderProps) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) {
      return stored === 'true';
    }
    return defaultCollapsed;
  });

  // Persist to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(storageKey, String(isCollapsed));
  }, [isCollapsed, storageKey]);

  const toggle = () => {
    setIsCollapsed(prev => !prev);
  };

  return (
    <RightSidebarContext.Provider value={{ isCollapsed, toggle }}>
      {children}
    </RightSidebarContext.Provider>
  );
};

/**
 * Hook to access right sidebar state
 * @param storageKey Unique key for localStorage persistence
 * @returns {isCollapsed, toggle}
 */
export const useRightSidebar = () => {
  const context = useContext(RightSidebarContext);
  if (!context) {
    throw new Error('useRightSidebar must be used within RightSidebarProvider');
  }
  return context;
};
